import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getUnreadBulletinCount } from '@/services/api';
import NotificationBell from '@/components/NotificationBell';
import OfflineQueueStatus from '@/components/OfflineQueueStatus';
import HelpAssistantBubble from '@/components/help/HelpAssistantBubble';
import OfflineStatusIndicator from '@/components/OfflineStatusIndicator';
import OrganizationSelector from '@/components/OrganizationSelector';
import BUSwitcher from '@/components/BUSwitcher';
import AppFooter from '@/components/AppFooter';
import BrandLogo from '@/components/BrandLogo';
import { 
  LayoutDashboard, FileWarning, AlertTriangle, ClipboardCheck,
  GraduationCap, FileText, Shield, BarChart3, Settings, Users,
  LogOut, Menu, X, Globe, ChevronDown, Building2, Megaphone, Activity, Plus, CalendarClock, GitBranch, Brain, Heart, Cpu, Plug, TrendingUp, Clock, Sun, Moon, Package, Send, Plane, Cog, Boxes, Warehouse, Share2, Eye, Search, Target, BookOpen, Scale, Wrench, Wand2, Calendar, Database, HelpCircle, Bot, Sparkles, Mic, HeartPulse, MessageSquare, Watch, Bell, Bird, FileEdit, Lightbulb, UserCheck, Award, Key, Network, LifeBuoy
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Language configuration
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦', nativeName: 'العربية' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷', nativeName: 'Português' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
  { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
];

// ==================== REGULATOR-ALIGNED MENU STRUCTURE ====================
// Based on: ICAO Annex 19 (4 SMS Pillars), GACAR Part 5, EASA ORO.GEN.200
// Role-based visibility: Each item can specify allowed roles OR (preferred)
// falls under a permission bucket via NAV_PERMISSION_MAP below.

// -- Permission-based access map (Feb 2026 RBAC migration) -----------------
// Every top-level menu group AND leaf id is mapped to one or more permission
// strings. If the current user's permissions[] array contains any of them,
// the item is shown — regardless of the legacy `roles: [...]` array on the
// item itself. This fixes the empty-sidebar bug for investigator, auditor,
// trainer and employee roles who were missing from most legacy arrays.
//
// Rule of thumb:
//   • Group parents  → the broadest permission of any child
//   • Individual leafs → the specific permission that domain requires
// Users with '*' (admin) always see everything.
const NAV_PERMISSION_MAP = {
  // Top-level groups (parents)
  'safety-reporting':     ['incidents.view', 'hazards.view', 'bulletins.view'],
  'risk-management':      ['risks.view', 'investigations.view'],
  'safety-assurance':     ['audits.view', 'findings.view', 'capa.view', 'spi.view', 'analytics.view'],
  'compliance-center':    ['compliance.view', 'documents.view'],
  'training-promotion':   ['training.view', 'surveys.view', 'gamification.view'],
  'assets':               ['assets.view'],

  // Safety Reporting leafs
  'incidents':            ['incidents.view'],
  'hazards':              ['hazards.view'],
  'occurrence-reports':   ['incidents.view'],
  'bulletins':            ['bulletins.view'],
  'safety-bulletins':     ['bulletins.view'],
  'safety-alerts':        ['bulletins.view'],
  'safety-recommendations': ['incidents.view', 'hazards.view'],
  'voice-report':         ['voice.report'],
  'fatigue-alerts':       ['frms.view', 'incidents.view'],
  'wildlife-strikes':     ['wildlife.view', 'hazards.view'],

  // Risk & Investigation
  'risks':                ['risks.view'],
  'risk-register':        ['risks.view'],
  'ai-risk':              ['risks.view', 'analytics.view'],
  'ai-assistant':         ['help.view'],
  'ai-query':             ['analytics.view'],
  'investigations':       ['investigations.view'],
  'investigation-templates': ['investigations.templates.manage', 'investigations.view'],
  'bowtie':               ['bowtie.edit', 'investigations.view'],

  // Assurance
  'audits':               ['audits.view'],
  'iosa-audit':           ['audits.view'],
  'audit-scenario':       ['audits.view'],
  'audit-programme':      ['audits.view'],
  'enterprise-audit':     ['audits.view'],
  'gaca-audit':           ['audits.view', 'compliance.view'],
  'capas':                ['capa.view', 'findings.view'],
  'findings':             ['findings.view'],
  'analytics':            ['analytics.view'],
  'bi-dashboard':         ['analytics.view'],
  'digital-twin':         ['analytics.view'],
  'spi':                  ['spi.view'],
  'evidence-library':     ['evidence.view'],

  // Compliance
  'compliance':           ['compliance.view'],
  'gap-analysis':         ['compliance.view'],
  'regulatory-api':       ['compliance.view', 'regulatory.submit'],
  'regulatory-reporting': ['regulatory.submit', 'compliance.view'],
  'ae-engagement':        ['compliance.view'],
  'resource-adequacy':    ['compliance.view'],
  'sms-implementation-plan': ['compliance.view'],
  'isms':                 ['compliance.view'],
  'documents':            ['documents.view'],
  'document-templates':   ['documents.templates.manage', 'documents.view'],
  'regulatory-resources': ['compliance.view', 'help.view'],
  'regulatory-reports':   ['regulatory.submit', 'compliance.view'],
  'reports':              ['evidence.view', 'analytics.view'],
  'report-builder':       ['analytics.view'],
  'scheduled-reports':    ['analytics.view'],

  // Training & Promotion
  'training':             ['training.view'],
  'training-modules':     ['training.view'],
  'training-compliance':  ['training.view', 'training.admin'],
  'training-currency':    ['training.view'],
  'gamification':         ['gamification.view'],
  'safety-culture-surveys': ['surveys.view'],
  'policies':             ['compliance.view', 'training.view'],

  // Assets
  'organizations':        ['org.manage', 'assets.view'],
  'aircraft':             ['assets.view'],
  'engines':              ['assets.view'],
  'components':           ['assets.view'],
  'gse':                  ['assets.view'],
  'maintenance':          ['assets.view'],
  'predictive-maintenance': ['assets.view'],
  'crew-management':      ['assets.view', 'users.manage'],
  'asset-analytics':      ['assets.view', 'analytics.view'],
  'data-sharing':         ['assets.view'],
  'mro-integration':      ['assets.view'],

  // Inspection & Administration
  'inspection-mode':      ['inspector.portal'],
  'workflow-builder':     ['settings.edit'],
  'users':                ['users.manage'],
  'user-management':      ['users.manage'],
  'admin-data-management':['data.purge', 'team.invite'],
  'admin-support':        ['*'],
  'admin-logs':           ['*'],
  'business-units':       ['org.manage'],
  'enterprise-user-management': ['users.manage'],
  'notification-settings':['notifications.manage'],
  'settings':             ['settings.edit', 'company.profile.edit'],
  'wearable-settings':    ['settings.edit'],

  // Cross-cutting
  'collaboration':        ['collab.view'],
  'help-center':          ['help.view'],
};

// Role definitions for menu access
// admin - Full access to everything
// safety_manager - Safety operations, reporting, risk, assurance
// quality_manager - Assurance, compliance, audits
// inspector - Inspection mode, compliance, read-only access
// pilot/crew - Reporting, training (operational staff)
// executive - Dashboard, high-level views

// 2️⃣ Safety Reporting - GACAR 5.5, ORO.GEN.200(a)(3)
const SAFETY_REPORTING_ITEMS = [
  { id: 'incidents', icon: FileWarning, labelKey: 'nav.hazardReports', roles: ['admin', 'safety_manager', 'quality_manager', 'pilot', 'inspector'] },
  { id: 'bulletins', icon: Megaphone, labelKey: 'nav.safetyBulletins', roles: ['admin', 'safety_manager', 'quality_manager', 'pilot', 'inspector', 'executive'] },
];

// 3️⃣ Risk Management - GACAR 5.6, ORO.GEN.200(a)(3)(ii)
const RISK_MANAGEMENT_ITEMS = [
  { id: 'risks', icon: AlertTriangle, labelKey: 'nav.riskRegister', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'risk-radar', icon: Activity, labelKey: 'nav.riskRadar', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'bowtie', icon: Target, labelKey: 'nav.bowtieAnalysis', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'investigation-templates', icon: HelpCircle, labelKey: 'nav.investigationTemplates', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'lessons-learned', icon: Lightbulb, labelKey: 'nav.lessonsLearned', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'inspector'] },
  { id: 'trends', icon: TrendingUp, labelKey: 'nav.riskTrends', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'changes', icon: GitBranch, labelKey: 'nav.managementOfChange', roles: ['admin', 'safety_manager', 'quality_manager'] },
  { id: 'meetings', icon: CalendarClock, labelKey: 'nav.safetyReviews', roles: ['admin', 'safety_manager', 'quality_manager', 'executive'] },
];

// 4️⃣ Safety Assurance - GACAR 5.7–5.8, ORO.GEN.200(a)(4)
const SAFETY_ASSURANCE_ITEMS = [
  { id: 'spis', icon: Target, labelKey: 'nav.safetyPerformance', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'emergency-response', icon: AlertTriangle, labelKey: 'nav.emergencyResponse', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'fdm', icon: Activity, labelKey: 'nav.fdmFoqa', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'frms', icon: Moon, labelKey: 'nav.frmsFatigue', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'biometric', icon: HeartPulse, labelKey: 'nav.biometricMonitoring', roles: ['admin', 'safety_manager', 'quality_manager', 'pilot', 'inspector'] },
  { id: 'fatigue-alerts', icon: AlertTriangle, labelKey: 'nav.fatigueAlerts', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'wildlife-strikes', icon: Bird, labelKey: 'nav.wildlifeStrikes', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'pilot', 'inspector'] },
  { id: 'ai-risk', icon: Brain, labelKey: 'nav.aiRiskPrediction', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'ai-assistant', icon: Bot, labelKey: 'nav.aiSafetyAssistant', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'pilot', 'inspector'] },
  { id: 'ai-query', icon: Sparkles, labelKey: 'nav.aiSafetyQuery', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'pilot', 'inspector'] },
  { id: 'audits', icon: ClipboardCheck, labelKey: 'nav.audits', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'iosa-audit', icon: Award, labelKey: 'nav.iosaAudit', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'audit-scenario', icon: Shield, labelKey: 'nav.auditSimulation', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'audit-programme', icon: Calendar, labelKey: 'nav.auditProgramme', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'enterprise-audit', icon: Building2, labelKey: 'nav.enterpriseAudit', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'capas', icon: Shield, labelKey: 'nav.findingsCapa', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'analytics', icon: TrendingUp, labelKey: 'nav.trendAnalysis', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'bi-dashboard', icon: BarChart3, labelKey: 'nav.biDashboard', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'digital-twin', icon: Cpu, labelKey: 'nav.digitalTwin', roles: ['admin', 'safety_manager', 'quality_manager'] },
];

// 5️⃣ Compliance - Clause mapping, Evidence, Gap Analysis
const COMPLIANCE_ITEMS = [
  { id: 'gap-analysis', icon: ClipboardCheck, labelKey: 'nav.gapAnalysis', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'regulatory-api', icon: Globe, labelKey: 'nav.regulatoryReporting', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'gaca-audit', icon: Award, labelKey: 'nav.gacaAudit', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'ae-engagement', icon: Award, labelKey: 'nav.aeEngagement', roles: ['admin', 'safety_manager', 'executive'] },
  { id: 'resource-adequacy', icon: Award, labelKey: 'nav.resourceAdequacy', roles: ['admin', 'safety_manager', 'executive'] },
  { id: 'sms-implementation-plan', icon: Award, labelKey: 'nav.smsImplementationPlan', roles: ['admin', 'safety_manager', 'executive'] },
  { id: 'isms', icon: Shield, labelKey: 'nav.cybersecurityIsms', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'documents', icon: FileText, labelKey: 'nav.documentControl', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'document-templates', icon: Wand2, labelKey: 'nav.documentTemplates', roles: ['admin', 'safety_manager', 'auditor'] },
  { id: 'compliance', icon: Scale, labelKey: 'nav.regulatoryMapping', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'regulatory-resources', icon: Globe, labelKey: 'nav.regulatoryResources', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'inspector'] },
  { id: 'regulatory-reports', icon: Send, labelKey: 'nav.authorityReporting', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'reports', icon: BarChart3, labelKey: 'nav.evidenceLibrary', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'report-builder', icon: FileEdit, labelKey: 'nav.reportBuilder', roles: ['admin', 'safety_manager', 'quality_manager'] },
  { id: 'scheduled-reports', icon: Clock, labelKey: 'nav.scheduledExports', roles: ['admin', 'safety_manager'] },
];

// 6️⃣ Training & Promotion - GACAR 5.9–5.10, ORO.GEN.200(a)(5)
const TRAINING_PROMOTION_ITEMS = [
  { id: 'training', icon: GraduationCap, labelKey: 'nav.smsTraining', roles: ['admin', 'safety_manager', 'quality_manager', 'pilot', 'inspector'] },
  { id: 'training-modules', icon: BookOpen, labelKey: 'nav.interactiveTraining', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'pilot', 'inspector'] },
  { id: 'training-compliance', icon: Shield, labelKey: 'nav.complianceReporting', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'training-currency', icon: Shield, labelKey: 'nav.trainingCurrency', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'gamification', icon: Sparkles, labelKey: 'nav.achievementsRewards', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'pilot', 'inspector'] },
  { id: 'safety-culture-surveys', icon: Heart, labelKey: 'nav.safetyCultureSurveys', roles: ['admin', 'safety_manager', 'quality_manager', 'pilot'] },
  { id: 'policies', icon: FileText, labelKey: 'nav.safetyPolicies', roles: ['admin', 'safety_manager', 'quality_manager', 'pilot', 'inspector', 'executive'] },
];

// 7️⃣ Assets - Supporting layer (not primary SMS pillar)
const ASSET_NAV_ITEMS = [
  { id: 'organizations', icon: Building2, labelKey: 'nav.organizations', roles: ['admin', 'safety_manager', 'quality_manager'] },
  { id: 'aircraft', icon: Plane, labelKey: 'nav.aircraft', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'engines', icon: Cog, labelKey: 'nav.engines', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'components', icon: Boxes, labelKey: 'nav.components', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'gse', icon: Warehouse, labelKey: 'nav.equipmentGse', roles: ['admin', 'safety_manager', 'quality_manager'] },
  { id: 'maintenance', icon: Wrench, labelKey: 'nav.maintenanceTracking', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'predictive-maintenance', icon: Sparkles, labelKey: 'nav.predictiveMaintenance', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'crew-management', icon: UserCheck, labelKey: 'nav.crewManagement', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector'] },
  { id: 'asset-analytics', icon: BarChart3, labelKey: 'nav.fleetAnalytics', roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  { id: 'data-sharing', icon: Share2, labelKey: 'nav.dataSharing', roles: ['admin', 'safety_manager'] },
  { id: 'mro-integration', icon: Wrench, labelKey: 'nav.mroIntegration', roles: ['admin', 'safety_manager'] },
];

// Main navigation - Regulator-Grade Structure with role-based access
const NAV_ITEMS = [
  // 1️⃣ Dashboard - Executive overview (all roles)
  { id: 'dashboard', icon: LayoutDashboard, labelKey: 'common.dashboard' },
  
  // 2️⃣ Safety Reporting - Hazard Identification (ICAO Pillar 2)
  { id: 'safety-reporting', icon: FileWarning, labelKey: 'nav.safetyReporting', isExpandable: true, subItems: SAFETY_REPORTING_ITEMS, roles: ['admin', 'safety_manager', 'quality_manager', 'pilot', 'inspector'] },
  
  // 3️⃣ Risk Management - SRM (ICAO Pillar 2)
  { id: 'risk-management', icon: AlertTriangle, labelKey: 'nav.riskManagement', isExpandable: true, subItems: RISK_MANAGEMENT_ITEMS, roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  
  // 4️⃣ Safety Assurance - SA (ICAO Pillar 3)
  { id: 'safety-assurance', icon: ClipboardCheck, labelKey: 'nav.safetyAssurance', isExpandable: true, subItems: SAFETY_ASSURANCE_ITEMS, roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  
  // 5️⃣ Compliance - Regulatory alignment
  { id: 'compliance-center', icon: Scale, labelKey: 'nav.compliance', isExpandable: true, subItems: COMPLIANCE_ITEMS, roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
  
  // 6️⃣ Training & Promotion - Safety Promotion (ICAO Pillar 4)
  { id: 'training-promotion', icon: BookOpen, labelKey: 'nav.trainingPromotion', isExpandable: true, subItems: TRAINING_PROMOTION_ITEMS, roles: ['admin', 'safety_manager', 'quality_manager', 'pilot', 'inspector'] },
  
  // 7️⃣ Assets - Supporting infrastructure
  { id: 'assets', icon: Package, labelKey: 'nav.assets', isExpandable: true, subItems: ASSET_NAV_ITEMS, roles: ['admin', 'safety_manager', 'quality_manager', 'inspector', 'executive'] },
];

// 8️⃣ Inspection Mode - For regulators (inspector and admin only)
const INSPECTION_ITEMS = [
  { id: 'inspection-mode', icon: Eye, labelKey: 'nav.inspectionMode', roles: ['admin', 'inspector'] },
];

// 8.5️⃣ Automation - Workflow Builder
const AUTOMATION_ITEMS = [
  { id: 'workflow-builder', icon: GitBranch, labelKey: 'nav.workflowBuilder', roles: ['admin', 'safety_manager'] },
];

// 9️⃣ Administration - Restricted access
const ADMIN_NAV_ITEMS = [
  { id: 'collaboration', icon: MessageSquare, labelKey: 'nav.collaborationHub', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'pilot', 'inspector'] },
  { id: 'org-management', icon: Building2, labelKey: 'nav.organizationManagement', roles: ['admin'] },
  { id: 'contractor-portal', icon: Wrench, labelKey: 'nav.contractorPortal', roles: ['admin', 'safety_manager', 'contractor'] },
  { id: 'regulator-dashboard', icon: Scale, labelKey: 'nav.regulatorDashboard', roles: ['admin', 'inspector', 'regulator'] },
  { id: 'ldap-sync', icon: Database, labelKey: 'nav.adLdapSync', roles: ['admin'] },
  { id: 'wearable-settings', icon: Watch, labelKey: 'nav.wearableSettings', roles: ['admin'] },
  { id: 'notification-settings', icon: Bell, labelKey: 'nav.notificationSettings', roles: ['admin'] },
  { id: 'admin-data-management', icon: Database, labelKey: 'nav.dataManagement', roles: ['admin'] },
  { id: 'admin-support', icon: LifeBuoy, labelKey: 'nav.supportInbox', roles: ['admin'] },
  { id: 'admin-logs', icon: FileText, labelKey: 'nav.logs', roles: ['admin'] },
  { id: 'business-units', icon: Network, labelKey: 'nav.businessUnits', roles: ['admin'] },
  { id: 'integrations', icon: Plug, labelKey: 'nav.integrations', roles: ['admin'] },
  { id: 'integration-config', icon: Key, labelKey: 'nav.apiConfiguration', roles: ['admin'] },
  { id: 'user-management', icon: Users, labelKey: 'nav.enterpriseUsers', roles: ['admin', 'safety_manager'] },
  { id: 'help-center', icon: HelpCircle, labelKey: 'nav.helpCenter', roles: ['admin', 'safety_manager', 'quality_manager', 'safety_officer', 'pilot', 'inspector', 'executive'] },
  { id: 'settings', icon: Settings, labelKey: 'common.settings', roles: ['admin'] },
];

export default function MainLayout({ children, currentPage, onNavigate, onOpenQuickReport }) {
  const { t } = useTranslation();
  const { api, user, logout, token } = useAuth();
  const { language, changeLanguage, isRTL } = useLanguage();
  const { theme, toggleTheme, isDark } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadBulletins, setUnreadBulletins] = useState({ unread: 0, urgent_unread: 0 });
  const [currentOrgId, setCurrentOrgId] = useState(null);
  // Default expand Safety Reporting and Risk Management for quick access
  const [expandedMenus, setExpandedMenus] = useState(['safety-reporting', 'risk-management']);
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Build searchable index of all menu items
  const searchableItems = useMemo(() => {
    const perms = user?.permissions || [];
    const isAdmin = perms.includes('*');
    const canSee = (it) => {
      if (isAdmin) return true;
      const mapped = NAV_PERMISSION_MAP[it.id];
      if (mapped) return mapped.some((p) => perms.includes(p));
      if (!it.roles) return true;
      return it.roles.includes(user?.role);
    };
    const items = [];
    
    // Add dashboard
    items.push({ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'Main' });
    
    // Add all nav items and their sub-items
    const addItems = (navItems, category) => {
      navItems.forEach(item => {
        if (item.subItems) {
          item.subItems.forEach(subItem => {
            if (canSee(subItem)) {
              items.push({
                id: subItem.id,
                label: subItem.labelKey,
                icon: subItem.icon,
                category: item.labelKey?.startsWith('common.') ? t(item.labelKey) : item.labelKey
              });
            }
          });
        } else if (!item.isExpandable) {
          if (canSee(item)) {
            items.push({
              id: item.id,
              label: item.labelKey?.startsWith('common.') ? t(item.labelKey) : item.labelKey,
              icon: item.icon,
              category: category
            });
          }
        }
      });
    };
    
    addItems(NAV_ITEMS, 'Navigation');
    addItems(AUTOMATION_ITEMS, 'Automation');
    addItems(INSPECTION_ITEMS, 'Regulator');
    addItems(ADMIN_NAV_ITEMS, 'Administration');
    
    return items;
  }, [user?.role, t]);

  // Filter search results based on query
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = searchableItems.filter(item => {
        // Search in label, category, and id
        const labelMatch = item.label.toLowerCase().includes(query);
        const categoryMatch = item.category.toLowerCase().includes(query);
        const idMatch = item.id.toLowerCase().includes(query);
        
        // Also match common aliases/keywords
        const aliases = {
          'incidents': ['incident', 'report', 'hazard', 'occurrence', 'safety report'],
          'risks': ['risk', 'hazard', 'danger', 'risk register'],
          'audits': ['audit', 'inspection', 'check', 'compliance'],
          'training': ['train', 'course', 'learn', 'education'],
          'training-modules': ['module', 'lesson', 'interactive', 'course'],
          'frms': ['fatigue', 'crew', 'rest', 'duty'],
          'biometric': ['biometric', 'fatigue', 'sleep', 'heart rate', 'hrv', 'wearable', 'health'],
          'fatigue-alerts': ['fatigue', 'alert', 'tired', 'rest', 'crew alert', 'risk alert'],
          'gamification': ['points', 'badges', 'leaderboard', 'achievements', 'rewards', 'score', 'level'],
          'capas': ['capa', 'finding', 'corrective', 'action'],
          'dashboard': ['home', 'overview', 'main'],
          'ai-assistant': ['ai', 'chat', 'assistant', 'help', 'bot'],
          'workflow-builder': ['workflow', 'automation', 'process', 'builder'],
          'user-management': ['user', 'permission', 'role', 'access'],
          'documents': ['document', 'file', 'doc'],
          'bulletins': ['bulletin', 'announcement', 'news', 'alert'],
          'isms': ['isms', 'cybersecurity', 'security', 'part-is', 'information security', 'cyber'],
          'collaboration': ['collaborate', 'comment', 'team', 'discuss', 'mention', 'pin', 'evidence'],
        };
        
        const itemAliases = aliases[item.id] || [];
        const aliasMatch = itemAliases.some(alias => alias.includes(query) || query.includes(alias));
        
        return labelMatch || categoryMatch || idMatch || aliasMatch;
      });
      setSearchResults(filtered);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchQuery, searchableItems]);

  // Handle search result click
  const handleSearchResultClick = (id) => {
    onNavigate(id);
    setSearchQuery('');
    setShowSearchResults(false);
    setMobileMenuOpen(false);
  };

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('[data-testid="sidebar-search-input"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
      // Close search results on Escape
      if (e.key === 'Escape' && showSearchResults) {
        setShowSearchResults(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearchResults]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSearchResults && !e.target.closest('[data-testid="sidebar-search-input"]') && !e.target.closest('[data-testid^="search-result-"]')) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSearchResults]);

  useEffect(() => {
    if (api) {
      fetchUnreadCount();
      // Refresh every 60 seconds
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [api]);

  const fetchUnreadCount = async () => {
    if (!api) return;
    try {
      const response = await getUnreadBulletinCount(api);
      setUnreadBulletins(response.data);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleNavClick = (id) => {
    onNavigate(id);
    setMobileMenuOpen(false);
    // Refresh unread count when navigating to bulletins
    if (id === 'bulletins') {
      setTimeout(fetchUnreadCount, 1000);
    }
  };

  const toggleExpandMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Access helper (Permission-Based RBAC, Feb 2026).
  // Priority order:
  //   1. Admin ('*' in permissions) → always show.
  //   2. NAV_PERMISSION_MAP → if the item's id has a mapping AND the user has
  //      any of those permissions, SHOW (even if their role isn't in item.roles).
  //   3. Legacy `roles: [...]` array → SHOW if user's role matches.
  //   4. No roles + no map entry → SHOW (broadly available items).
  // This gracefully upgrades the sidebar for investigator, auditor, trainer,
  // employee (previously missing from most role arrays) without touching the
  // 100+ existing item definitions.
  const userPerms = user?.permissions || [];
  const isSuperAdmin = userPerms.includes('*');

  const hasAccess = (item) => {
    if (isSuperAdmin) return true;
    const mapped = NAV_PERMISSION_MAP[item.id];
    if (mapped) {
      // When a permission mapping exists it is AUTHORITATIVE — legacy
      // `roles: [...]` arrays are intentionally ignored so a stale role
      // string can't leak access. Non-mapped items fall back to the legacy
      // roles array (or are open by default).
      return mapped.some((p) => userPerms.includes(p));
    }
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  };

  const filteredAdminItems = ADMIN_NAV_ITEMS.filter(hasAccess);

  const filteredAutomationItems = AUTOMATION_ITEMS.filter(hasAccess);

  // Helper to find page label from any menu structure
  const getPageTitle = (pageId) => {
    if (pageId === 'dashboard') return t('common.dashboard');
    
    // Check main items
    const mainItem = NAV_ITEMS.find(i => i.id === pageId);
    if (mainItem) {
      return t(mainItem.labelKey);
    }
    
    // Check sub-items in expandable menus
    for (const item of NAV_ITEMS) {
      if (item.subItems) {
        const subItem = item.subItems.find(s => s.id === pageId);
        if (subItem) {
          return t(subItem.labelKey);
        }
      }
    }
    
    // Check admin items
    const adminItem = ADMIN_NAV_ITEMS.find(i => i.id === pageId);
    if (adminItem) {
      return t(adminItem.labelKey);
    }
    
    // Check inspection items
    const inspectionItem = INSPECTION_ITEMS.find(i => i.id === pageId);
    if (inspectionItem) {
      return t(inspectionItem.labelKey);
    }
    
    // Fallback: capitalize the page ID
    return pageId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Filter nav items by role, including sub-items
  const getFilteredNavItems = () => {
    return NAV_ITEMS.filter(hasAccess).map(item => {
      if (item.subItems) {
        return {
          ...item,
          subItems: item.subItems.filter(hasAccess)
        };
      }
      return item;
    }).filter(item => !item.subItems || item.subItems.length > 0); // Remove empty expandable menus
  };

  const filteredNavItems = getFilteredNavItems();
  const filteredInspectionItems = INSPECTION_ITEMS.filter(hasAccess);

  // Check if any sub-item is active (non-recursive)
  const isSubItemActive = (item) => {
    if (!item.subItems) return false;
    return item.subItems.some(sub => currentPage === sub.id);
  };

  // Simple sub-item renderer (no recursion)
  const SubNavItem = ({ subItem, mobile }) => {
    const SubIcon = subItem.icon;
    const isActive = currentPage === subItem.id;
    
    return (
      <button
        key={subItem.id}
        onClick={() => handleNavClick(subItem.id)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2 rounded-sm text-xs font-medium transition-colors relative",
          isActive 
            ? "bg-sky-600/20 text-sky-500 border-l-2 border-sky-500" 
            : isDark 
              ? "text-slate-400 hover:text-white hover:bg-slate-800/50"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
          isRTL && "flex-row-reverse border-l-0 border-r-2",
          mobile && "text-base py-3"
        )}
        data-testid={`nav-${subItem.id}`}
      >
        <SubIcon className="w-4 h-4 flex-shrink-0" />
        {(sidebarOpen || mobile) && <span>{t(subItem.labelKey)}</span>}
      </button>
    );
  };

  const NavItem = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const hasActiveSubItem = item.subItems ? item.subItems.some(sub => currentPage === sub.id) : false;
    const isActive = currentPage === item.id || hasActiveSubItem;
    const showBadge = item.id === 'bulletins' && unreadBulletins.unread > 0;
    const hasUrgent = item.id === 'bulletins' && unreadBulletins.urgent_unread > 0;
    const isExpanded = expandedMenus.includes(item.id);
    
    if (item.isExpandable && item.subItems) {
      return (
        <div className="w-full">
          <button
            onClick={() => toggleExpandMenu(item.id)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors relative",
              isActive 
                ? "bg-sky-600/20 text-sky-500" 
                : isDark 
                  ? "text-slate-400 hover:text-white hover:bg-slate-800/50"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
              isRTL && "flex-row-reverse",
              mobile && "text-base py-3"
            )}
            data-testid={`nav-${item.id}`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {(sidebarOpen || mobile) && (
              <span className="flex-1 flex items-center justify-between">
                <span>{t(item.labelKey)}</span>
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform",
                  isExpanded && "rotate-180"
                )} />
              </span>
            )}
          </button>
          {isExpanded && (sidebarOpen || mobile) && (
            <div className={cn("ml-4 border-l", isDark ? "border-slate-700" : "border-gray-200")}>
              {item.subItems.map(subItem => (
                <SubNavItem key={subItem.id} subItem={subItem} mobile={mobile} />
              ))}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <button
        onClick={() => handleNavClick(item.id)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors relative",
          isActive 
            ? "bg-sky-600/20 text-sky-500 border-l-2 border-sky-500" 
            : isDark 
              ? "text-slate-400 hover:text-white hover:bg-slate-800/50"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
          isRTL && "flex-row-reverse border-l-0 border-r-2",
          mobile && "text-base py-3"
        )}
        data-testid={`nav-${item.id}`}
      >
        <div className="relative">
          <Icon className="w-5 h-5 flex-shrink-0" />
          {showBadge && !sidebarOpen && !mobile && (
            <span className={cn(
              "absolute -top-1 -right-1 w-2 h-2 rounded-full",
              hasUrgent ? "bg-red-500 animate-pulse" : "bg-sky-500"
            )} />
          )}
        </div>
        {(sidebarOpen || mobile) && (
          <span className="flex-1 flex items-center justify-between">
            <span>{t(item.labelKey)}</span>
            {showBadge && (
              <span 
                className={cn(
                  "ml-2 px-1.5 py-0.5 text-xs font-bold rounded-full min-w-[20px] text-center",
                  hasUrgent 
                    ? "bg-red-600 text-white animate-pulse" 
                    : "bg-sky-600 text-white"
                )}
                data-testid="bulletins-badge"
              >
                {unreadBulletins.unread > 99 ? '99+' : unreadBulletins.unread}
              </span>
            )}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={cn(
      "min-h-screen flex",
      isDark ? "bg-slate-950" : "bg-gray-50",
      isRTL && "flex-row-reverse"
    )}>
      {/* Desktop Sidebar */}
      <aside 
        className={cn(
          "hidden lg:flex flex-col backdrop-blur-sm transition-all duration-300",
          isDark ? "bg-slate-900/80 border-r border-slate-800" : "bg-white border-r border-gray-200",
          sidebarOpen ? "w-64" : "w-20",
          isRTL && "border-r-0 border-l"
        )}
      >
        {/* Logo */}
        <div className={cn(
          "h-16 flex items-center px-4",
          isDark ? "border-b border-slate-800" : "border-b border-gray-200",
          !sidebarOpen && "justify-center"
        )}>
          <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
            <BrandLogo variant="mark" className="w-10 h-10 shrink-0" />
            {sidebarOpen && (
              <div>
                <h1 className={cn("text-lg font-bold font-barlow tracking-tight", isDark ? "text-white" : "text-gray-900")}>AeroGuard</h1>
                <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>SMS</p>
              </div>
            )}
          </div>
        </div>

        {/* Search Input */}
        {sidebarOpen && (
          <div className="px-3 py-3 relative">
            <div className="relative">
              <Search className={cn(
                "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4",
                isDark ? "text-slate-500" : "text-gray-400"
              )} />
              <Input
                type="text"
                placeholder="Search menus..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && setShowSearchResults(true)}
                className={cn(
                  "pl-9 pr-16 h-9 text-sm",
                  isDark 
                    ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-sky-500" 
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-sky-500"
                )}
                data-testid="sidebar-search-input"
              />
              {!searchQuery && (
                <span className={cn(
                  "absolute right-2 top-1/2 -translate-y-1/2 text-xs px-1.5 py-0.5 rounded border",
                  isDark ? "text-slate-500 border-slate-600 bg-slate-800" : "text-gray-400 border-gray-300 bg-gray-100"
                )}>
                  ⌘K
                </span>
              )}
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setShowSearchResults(false); }}
                  className={cn(
                    "absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-700/50",
                    isDark ? "text-slate-400" : "text-gray-400"
                  )}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className={cn(
                "absolute left-3 right-3 top-full mt-1 rounded-lg shadow-lg border z-50 max-h-80 overflow-y-auto",
                isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
              )}>
                {searchResults.map((result, idx) => {
                  const ResultIcon = result.icon;
                  return (
                    <button
                      key={result.id}
                      onClick={() => handleSearchResultClick(result.id)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors",
                        isDark 
                          ? "hover:bg-slate-700/50 text-slate-200" 
                          : "hover:bg-gray-50 text-gray-700",
                        idx !== searchResults.length - 1 && (isDark ? "border-b border-slate-700" : "border-b border-gray-100")
                      )}
                      data-testid={`search-result-${result.id}`}
                    >
                      <ResultIcon className={cn("w-4 h-4 flex-shrink-0", isDark ? "text-sky-400" : "text-sky-600")} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{result.label}</p>
                        <p className={cn("text-xs truncate", isDark ? "text-slate-500" : "text-gray-400")}>
                          {result.category}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* No Results */}
            {showSearchResults && searchQuery && searchResults.length === 0 && (
              <div className={cn(
                "absolute left-3 right-3 top-full mt-1 rounded-lg shadow-lg border z-50 p-4 text-center",
                isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
              )}>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                  No results found for "{searchQuery}"
                </p>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {/* Main SMS Navigation - Role-filtered */}
            {filteredNavItems.map(item => (
              <NavItem key={item.id} item={item} />
            ))}
            
            {/* Automation - Workflow Builder */}
            {filteredAutomationItems.length > 0 && (
              <>
                <div className={cn("my-4 border-t", isDark ? "border-slate-800" : "border-gray-200")} />
                {sidebarOpen && (
                  <p className={cn("px-3 py-1 text-xs font-semibold uppercase tracking-wider", isDark ? "text-slate-500" : "text-gray-400")}>
                    Automation
                  </p>
                )}
                {filteredAutomationItems.map(item => (
                  <NavItem key={item.id} item={item} />
                ))}
              </>
            )}
            
            {/* Inspection Mode - For regulators */}
            {filteredInspectionItems.length > 0 && (
              <>
                <div className={cn("my-4 border-t", isDark ? "border-slate-800" : "border-gray-200")} />
                {sidebarOpen && (
                  <p className={cn("px-3 py-1 text-xs font-semibold uppercase tracking-wider", isDark ? "text-slate-500" : "text-gray-400")}>
                    Regulator Access
                  </p>
                )}
                {filteredInspectionItems.map(item => (
                  <NavItem key={item.id} item={item} />
                ))}
              </>
            )}
            
            {/* Administration - Restricted */}
            {filteredAdminItems.length > 0 && (
              <>
                <div className={cn("my-4 border-t", isDark ? "border-slate-800" : "border-gray-200")} />
                {sidebarOpen && (
                  <p className={cn("px-3 py-1 text-xs font-semibold uppercase tracking-wider", isDark ? "text-slate-500" : "text-gray-400")}>
                    Administration
                  </p>
                )}
                {filteredAdminItems.map(item => (
                  <NavItem key={item.id} item={item} />
                ))}
              </>
            )}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className={cn("p-4", isDark ? "border-t border-slate-800" : "border-t border-gray-200")}>
          {sidebarOpen ? (
            <div className="space-y-3">
              <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center",
                  isDark ? "bg-slate-700" : "bg-gray-200"
                )}>
                  <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-gray-900")}>
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-medium truncate", isDark ? "text-white" : "text-gray-900")}>{user?.name}</p>
                  <p className={cn("text-xs capitalize", isDark ? "text-slate-500" : "text-gray-500")}>{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "flex-1",
                        isDark ? "text-slate-400 hover:text-white hover:bg-slate-800" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      )}
                      data-testid="sidebar-language-toggle"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      {LANGUAGES.find(l => l.code === language)?.flag || '🌐'} {language.toUpperCase()}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    {LANGUAGES.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={cn(
                          "flex items-center gap-2 cursor-pointer",
                          language === lang.code && "bg-primary/10"
                        )}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.nativeName}</span>
                        {language === lang.code && <span className="ml-auto text-primary">✓</span>}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className={cn(
                    "flex-1",
                    isDark ? "text-slate-400 hover:text-red-400 hover:bg-red-900/20" : "text-gray-600 hover:text-red-600 hover:bg-red-50"
                  )}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 flex flex-col items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className={cn("w-full", isDark ? "text-slate-400 hover:text-red-400" : "text-gray-600 hover:text-red-600")}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={cn(
            "absolute top-20 -right-3 w-6 h-6 rounded-full flex items-center justify-center transition-colors",
            isDark ? "bg-slate-800 border border-slate-700 text-slate-400 hover:text-white" : "bg-white border border-gray-300 text-gray-400 hover:text-gray-600 shadow-sm",
            isRTL && "right-auto -left-3"
          )}
          data-testid="sidebar-toggle"
        >
          <ChevronDown className={cn(
            "w-4 h-4 transition-transform",
            sidebarOpen ? (isRTL ? "rotate-90" : "-rotate-90") : (isRTL ? "-rotate-90" : "rotate-90")
          )} />
        </button>
      </aside>

      {/* Main Content Area with Top Header */}
      <div className="flex-1 flex flex-col">
        {/* Top Header Bar - Desktop */}
        <header className={cn(
          "hidden lg:flex h-14 items-center justify-between px-6 border-b",
          isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200"
        )}>
          <div className="flex items-center gap-4">
            <h2 className={cn("text-lg font-semibold font-barlow", isDark ? "text-white" : "text-gray-900")}>
              {getPageTitle(currentPage)}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Business Unit Switcher (hidden when < 2 accessible BUs) */}
            <BUSwitcher />
            {/* Organization Selector */}
            <OrganizationSelector 
              currentOrgId={currentOrgId} 
              onOrgChange={setCurrentOrgId} 
            />
            {/* Offline Queue Status */}
            <OfflineQueueStatus />
            {/* Offline Storage Status */}
            <OfflineStatusIndicator />
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={cn(
                "rounded-full",
                isDark ? "text-slate-400 hover:text-white hover:bg-slate-800" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              )}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            {/* Notification Bell */}
            <NotificationBell />
            {/* User Avatar */}
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center",
              isDark ? "bg-slate-700" : "bg-gray-200"
            )}>
              <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-gray-900")}>
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        {/* Mobile Header & Menu */}
        <div className={cn(
          "lg:hidden fixed top-0 left-0 right-0 z-50 backdrop-blur-sm",
          isDark ? "bg-slate-900/95 border-b border-slate-800" : "bg-white/95 border-b border-gray-200"
        )}>
        <div className="h-16 flex items-center justify-between px-4">
          <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
            <BrandLogo variant="mark" className="w-8 h-8" />
            <h1 className={cn("text-lg font-bold font-barlow", isDark ? "text-white" : "text-gray-900")}>AeroGuard</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Offline Queue - Mobile */}
            <OfflineQueueStatus />
            {/* Theme Toggle - Mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={cn(isDark ? "text-slate-400" : "text-gray-600")}
              data-testid="theme-toggle-mobile"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={cn(isDark ? "text-white" : "text-gray-900")}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={cn(
            "px-4 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto",
            isDark ? "bg-slate-900 border-b border-slate-800" : "bg-white border-b border-gray-200"
          )}>
            {/* Mobile Search */}
            <div className="mb-4 relative">
              <div className="relative">
                <Search className={cn(
                  "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4",
                  isDark ? "text-slate-500" : "text-gray-400"
                )} />
                <Input
                  type="text"
                  placeholder="Search menus..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery && setShowSearchResults(true)}
                  className={cn(
                    "pl-9 pr-8 h-10 text-base",
                    isDark 
                      ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500" 
                      : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                  )}
                  data-testid="mobile-sidebar-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setShowSearchResults(false); }}
                    className={cn(
                      "absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded",
                      isDark ? "text-slate-400" : "text-gray-400"
                    )}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Mobile Search Results */}
              {showSearchResults && searchResults.length > 0 && (
                <div className={cn(
                  "mt-2 rounded-lg border max-h-60 overflow-y-auto",
                  isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
                )}>
                  {searchResults.map((result, idx) => {
                    const ResultIcon = result.icon;
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSearchResultClick(result.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-3 text-left transition-colors",
                          isDark 
                            ? "hover:bg-slate-700/50 text-slate-200" 
                            : "hover:bg-gray-50 text-gray-700",
                          idx !== searchResults.length - 1 && (isDark ? "border-b border-slate-700" : "border-b border-gray-100")
                        )}
                      >
                        <ResultIcon className={cn("w-5 h-5 flex-shrink-0", isDark ? "text-sky-400" : "text-sky-600")} />
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-medium truncate">{result.label}</p>
                          <p className={cn("text-sm truncate", isDark ? "text-slate-500" : "text-gray-400")}>
                            {result.category}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {showSearchResults && searchQuery && searchResults.length === 0 && (
                <div className={cn(
                  "mt-2 rounded-lg border p-4 text-center",
                  isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
                )}>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                    No results found
                  </p>
                </div>
              )}
            </div>
            
            <nav className="space-y-1">
              {/* Main SMS Navigation - Role-filtered */}
              {filteredNavItems.map(item => (
                <NavItem key={item.id} item={item} mobile />
              ))}
              
              {/* Automation - Mobile */}
              {filteredAutomationItems.length > 0 && (
                <>
                  <div className={cn("my-3 border-t", isDark ? "border-slate-800" : "border-gray-200")} />
                  <p className={cn("px-3 py-1 text-xs font-semibold uppercase", isDark ? "text-slate-500" : "text-gray-400")}>
                    Automation
                  </p>
                  {filteredAutomationItems.map(item => (
                    <NavItem key={item.id} item={item} mobile />
                  ))}
                </>
              )}
              
              {/* Inspection Mode */}
              {filteredInspectionItems.length > 0 && (
                <>
                  <div className={cn("my-3 border-t", isDark ? "border-slate-800" : "border-gray-200")} />
                  <p className={cn("px-3 py-1 text-xs font-semibold uppercase", isDark ? "text-slate-500" : "text-gray-400")}>
                    Regulator Access
                  </p>
                  {filteredInspectionItems.map(item => (
                    <NavItem key={item.id} item={item} mobile />
                  ))}
                </>
              )}
              
              {/* Administration */}
              {filteredAdminItems.length > 0 && (
                <>
                  <div className={cn("my-3 border-t", isDark ? "border-slate-800" : "border-gray-200")} />
                  <p className={cn("px-3 py-1 text-xs font-semibold uppercase", isDark ? "text-slate-500" : "text-gray-400")}>
                    Administration
                  </p>
                  {filteredAdminItems.map(item => (
                    <NavItem key={item.id} item={item} mobile />
                  ))}
                </>
              )}
            </nav>
            <div className={cn(
              "mt-4 pt-4 flex gap-2",
              isDark ? "border-t border-slate-800" : "border-t border-gray-200"
            )}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "flex-1",
                      isDark ? "border-slate-700 text-slate-300" : "border-gray-300 text-gray-600"
                    )}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    {LANGUAGES.find(l => l.code === language)?.flag} {LANGUAGES.find(l => l.code === language)?.nativeName}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-40">
                  {LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={cn(
                        "flex items-center gap-2 cursor-pointer",
                        language === lang.code && "bg-primary/10"
                      )}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.nativeName}</span>
                      {language === lang.code && <span className="ml-auto text-primary">✓</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className={cn(
                  "flex-1",
                  isDark ? "border-slate-700 text-red-400" : "border-gray-300 text-red-600"
                )}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
        )}
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="min-h-full flex flex-col">
            <div className="flex-1">{children}</div>
            <AppFooter onNavigate={(page) => { window.location.hash = `#${page}`; }} />
          </div>
        </main>
      </div>

      {/* Mobile Floating Action Button for Quick Hazard Report */}
      {onOpenQuickReport && (
        <button
          onClick={onOpenQuickReport}
          className="lg:hidden fixed bottom-20 right-6 w-14 h-14 rounded-full bg-red-600 text-white shadow-lg shadow-red-900/50 flex items-center justify-center z-50 active:scale-95 transition-transform"
          data-testid="quick-report-fab"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
      {/* AeroGuard AI Help Assistant — floating bubble on every page */}
      <HelpAssistantBubble token={token} role={user?.role} lang={language} />
    </div>
  );
}
